/**
 * PWA helpers — service worker registration, push subscribe/unsubscribe,
 * install-prompt deferral.
 */
import { apiClient } from "./api";

const SW_PATH = "/sw.js";
const INSTALL_PROMPT_KEY = "pwa_install_dismissed_at";
const PUSH_PROMPT_KEY = "pwa_push_dismissed_at";
const DISMISS_COOLDOWN_DAYS = 7;

// ─── Service worker registration ──────────────────────────────────────────
export async function registerServiceWorker() {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return null;
  // Disable SW on localhost dev to avoid stale caches while iterating.
  if (window.location.hostname === "localhost") return null;
  try {
    const reg = await navigator.serviceWorker.register(SW_PATH, { scope: "/" });
    return reg;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("SW registration failed:", e?.message);
    return null;
  }
}

// ─── Install prompt management ────────────────────────────────────────────
let _deferredInstall = null;

export function watchInstallPrompt(onChange) {
  if (typeof window === "undefined") return () => {};
  const handler = (e) => {
    e.preventDefault();
    _deferredInstall = e;
    onChange?.(true);
  };
  const installed = () => {
    _deferredInstall = null;
    onChange?.(false);
  };
  window.addEventListener("beforeinstallprompt", handler);
  window.addEventListener("appinstalled", installed);
  return () => {
    window.removeEventListener("beforeinstallprompt", handler);
    window.removeEventListener("appinstalled", installed);
  };
}

export async function fireInstallPrompt() {
  if (!_deferredInstall) return { outcome: "unavailable" };
  _deferredInstall.prompt();
  const choice = await _deferredInstall.userChoice;
  _deferredInstall = null;
  return choice;
}

export function isInstallDismissed() {
  return _isDismissed(INSTALL_PROMPT_KEY);
}
export function dismissInstall() {
  _dismiss(INSTALL_PROMPT_KEY);
}

export function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

// ─── Push subscription ────────────────────────────────────────────────────
function urlBase64ToUint8Array(b64) {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function pushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export async function getCurrentPushSubscription() {
  if (!pushSupported()) return null;
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return null;
  return await reg.pushManager.getSubscription();
}

export async function subscribePush() {
  if (!pushSupported()) throw new Error("Push not supported on this device");
  if (Notification.permission === "denied")
    throw new Error("Notifications are blocked in your browser settings. Enable them and try again.");
  const reg = await navigator.serviceWorker.ready;

  // Permission
  if (Notification.permission !== "granted") {
    const perm = await Notification.requestPermission();
    if (perm !== "granted") throw new Error("Notification permission denied");
  }

  // VAPID key
  const { data } = await apiClient.get("/push/vapid-public-key");
  const key = urlBase64ToUint8Array(data.key);

  // Existing sub? unsubscribe to reset (covers key rotations)
  const existing = await reg.pushManager.getSubscription();
  if (existing) {
    try {
      await existing.unsubscribe();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[pwa] could not unsubscribe existing push sub:", e?.message);
    }
  }
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: key,
  });

  const json = sub.toJSON();
  await apiClient.post("/push/subscribe", {
    endpoint: json.endpoint,
    p256dh: json.keys?.p256dh,
    auth: json.keys?.auth,
  });
  return sub;
}

export async function unsubscribePush() {
  const sub = await getCurrentPushSubscription();
  if (!sub) return false;
  try {
    await apiClient.post("/push/unsubscribe", { endpoint: sub.endpoint });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[pwa] backend unsubscribe call failed:", e?.message);
  }
  try {
    await sub.unsubscribe();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[pwa] browser unsubscribe failed:", e?.message);
  }
  return true;
}

export function isPushDismissed() {
  return _isDismissed(PUSH_PROMPT_KEY);
}
export function dismissPush() {
  _dismiss(PUSH_PROMPT_KEY);
}

// ─── helpers ──────────────────────────────────────────────────────────────
// NOTE: localStorage usage in this file only stores millisecond UI-cooldown
// timestamps for "install" and "push" dismissal banners. No tokens, sessions,
// or PII are stored — this is safe even with XSS concerns.
function _isDismissed(key) {
  try {
    const ts = parseInt(localStorage.getItem(key) || "0", 10);
    if (!ts) return false;
    return Date.now() - ts < DISMISS_COOLDOWN_DAYS * 24 * 60 * 60 * 1000;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[pwa] localStorage.getItem failed (private mode?):", e?.message);
    return false;
  }
}
function _dismiss(key) {
  try {
    // Stores a timestamp only — non-sensitive UI cooldown marker, safe for localStorage.
    localStorage.setItem(key, String(Date.now()));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[pwa] localStorage.setItem failed (private mode?):", e?.message);
  }
}
